/**
 * KAN-752: на странице Telegram-аккаунтов вместо блока вариантов приветствия -
 * ссылки на страницы операторов рассылки. Варианты и статистика A/B живут на
 * странице оператора (/users/:id?tab=greetings), здесь их не дублируем.
 *
 * Операторы рассылки: из загруженного списка аккаунтов и включённые операторы
 * канала telegram_user (кампания без аккаунтов тоже получает ссылку). Подпись -
 * имя оператора, email только если имени нет; служебные email кампаний скрыты.
 * Блок для владельца кабинета: не-владелец настраивает свои приветствия прямо
 * на странице аккаунтов (GreetingVariantsBlock).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useChannelOperators } from '@/hooks/useChannelOperators';
import { greetingOperators, greetingsHref, type AccountOperator } from '@/lib/greetingOperators';
import { TELEGRAM_USER_CHANNEL } from '@/services/api/channelOperators';

export const GREETING_OPERATORS_NOTE = 'Приветствия настраиваются на странице оператора';

interface GreetingOperatorsLinkProps {
  accounts: ReadonlyArray<AccountOperator>;
}

export function GreetingOperatorsLink({ accounts }: GreetingOperatorsLinkProps): JSX.Element {
  const { operators: channelOperators } = useChannelOperators(TELEGRAM_USER_CHANNEL);
  const operators = useMemo(
    () => greetingOperators(accounts, channelOperators),
    [accounts, channelOperators]
  );

  return (
    <section data-testid="greeting-operators-link" className="mt-6">
      <h3 className="mb-1 text-base font-semibold text-gray-900">A/B первых сообщений</h3>
      <p className="mb-2 text-sm text-gray-600">
        {GREETING_OPERATORS_NOTE}, вкладка "Приветствия A/B". У каждого оператора (кампании) свои
        варианты и своя статистика.
      </p>
      {operators.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {operators.map((op) => (
            <li key={op.id}>
              <Link
                to={greetingsHref(op.id)}
                data-testid={`greeting-operator-${op.id}`}
                className="inline-flex rounded-md border px-2 py-1 text-sm text-blue-700 hover:bg-blue-50"
              >
                {op.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link to="/users" data-testid="greeting-operators-all" className="text-sm text-blue-700 hover:underline">
        Все операторы и кампании
      </Link>
    </section>
  );
}

export default GreetingOperatorsLink;
